import { useNavigate } from 'react-router';
import { Eye, FileSignature } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DataLoader } from '@/components/ui/DataLoader';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useCarryDocuments } from '../hooks/useCarry';
import type { CarryMemberFundDocumentsSummary } from '../services/carryService';
import { OpenToSignCard } from '@/components/carry-documents/OpenToSignCard';
import { AWAITING_YOUR_SIGNATURE } from './carryDocumentListValues';
import { CarryDocumentStatusBadge } from '@/components/carry-documents/CarryDocumentStatusBadge';
import {
  tallyOf,
  tallyStatus,
} from '@/components/carry-documents/carryDocumentStatus';
import type { CarryFundDocumentsSummary } from '@/types/carryDocument';
import { portalCarryDocumentsPath } from '../carryPaths';

function FundsTable({ funds }: { funds: CarryFundDocumentsSummary[] }) {
  const navigate = useNavigate();

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Fund</TableHead>
          <TableHead className="text-center">Signed</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {funds.map((fund) => {
          const total = tallyOf(fund.document_statuses);
          return (
            <TableRow
              key={fund.fund_id}
              onClick={() => navigate(portalCarryDocumentsPath(fund.fund_id))}
              className="cursor-pointer hover:bg-muted/50"
            >
              <TableCell className="font-medium">{fund.fund_name}</TableCell>
              <TableCell className="text-center">
                {total.signed}/{total.required}
              </TableCell>
              <TableCell>
                {total.required === 0 ? (
                  '-'
                ) : (
                  <CarryDocumentStatusBadge status={tallyStatus(total)} />
                )}
              </TableCell>
              <TableCell>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`View documents of ${fund.fund_name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(portalCarryDocumentsPath(fund.fund_id));
                  }}
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function DocumentsOverview({
  funds,
}: {
  funds: CarryMemberFundDocumentsSummary[];
}) {
  const openToSign = funds.reduce((sum, f) => sum + f.awaiting_my_signature, 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileSignature className="h-5 w-5" />
          <span>Documents</span>
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Only the funds you participate in. Open one to see its documents.
        </p>
        <div className="pt-4">
          <OpenToSignCard
            count={openToSign}
            description={AWAITING_YOUR_SIGNATURE}
          />
        </div>
      </CardHeader>
      <CardContent>
        <FundsTable funds={funds} />
      </CardContent>
    </Card>
  );
}

export function CarryDocumentsTab() {
  const documentsQuery = useCarryDocuments();

  return (
    <DataLoader<CarryMemberFundDocumentsSummary[]>
      query={documentsQuery}
      showEmptyState={(funds) => funds.length === 0}
      emptyComponent={
        <p className="py-6 text-center text-sm text-muted-foreground">
          You do not participate in any fund carry yet.
        </p>
      }
    >
      {(funds) => <DocumentsOverview funds={funds} />}
    </DataLoader>
  );
}
